import { useBooth } from "../BoothContext";
import type { CaptureMode } from "../BoothContext";
import { IconCamera, IconSparkles } from "@/features/admin/components/icons";

/**
 * Which product does the guest want?
 *
 * Only shown when the event offers both, and the trade-off is stated plainly:
 * AI is better but slow, a snapshot is instant. At a booth with a queue behind
 * you, that's the decision that actually matters — so it's made honestly and
 * up front rather than after a minute of waiting.
 */
export function ModeScreen() {
  const booth = useBooth();
  const pricing = booth.config.pricing;

  // Quote the download price: it's the action almost everyone takes, and a
  // number here is more honest than "from free".
  const quote = (kind: "ai_photo" | "snapshot") => {
    const cell = pricing?.grid?.[kind]?.download;
    return cell?.payable && cell.cents > 0
      ? `${(cell.cents / 100).toFixed(2)} ${pricing?.currency ?? ""}`.trim()
      : "Free";
  };

  const choose = (mode: CaptureMode) => {
    booth.setMode(mode);
    booth.go(booth.stepAfterMode);
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 px-6 py-20">
      <header className="text-center">
        <h2 className="text-4xl font-bold uppercase tracking-tight" style={{ color: "var(--booth-headline)" }}>
          How do you want it?
        </h2>
        <p className="mt-2 text-base opacity-70" style={{ color: "var(--booth-subtitle)" }}>
          Pick a style, then step in front of the camera
        </p>
      </header>

      <div className="grid w-full max-w-2xl gap-4 sm:grid-cols-2">
        {booth.availableModes.includes("ai_photo_booth") && (
          <ModeCard
            icon={<IconSparkles size={22} />}
            title="AI Photo"
            subtitle="Reimagined"
            body="We rebuild your photo in the scene you choose. Takes about a minute."
            badge={quote("ai_photo")}
            onClick={() => choose("ai_photo_booth")}
          />
        )}

        {booth.availableModes.includes("snapshot") && (
          <ModeCard
            icon={<IconCamera size={22} />}
            title="Snapshot"
            subtitle="Instant"
            body="Your photo with a filter, ready straight away."
            badge={quote("snapshot")}
            highlight
            onClick={() => choose("snapshot")}
          />
        )}
      </div>
    </div>
  );
}

function ModeCard({
  icon, title, subtitle, body, badge, highlight, onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  body: string;
  badge: string;
  highlight?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col gap-3 rounded-2xl p-6 text-left shadow-lg transition-transform active:scale-[0.98]"
      style={{
        background: highlight ? "var(--booth-secondary)" : "var(--booth-surface)",
        color: highlight ? "var(--booth-cta-text)" : "var(--booth-ink)",
      }}
    >
      <span className="flex items-center justify-between">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--booth-cta-scrim)]">{icon}</span>
        <span className="rounded-full bg-[var(--booth-cta-scrim)] px-3 py-1 text-xs font-bold uppercase tracking-wide">
          {badge}
        </span>
      </span>
      <span className="flex flex-col">
        <span className="text-2xl font-bold uppercase leading-tight">{title}</span>
        <span className="text-xs uppercase tracking-[0.2em] opacity-70">{subtitle}</span>
      </span>
      <span className="text-sm leading-relaxed opacity-80">{body}</span>
    </button>
  );
}
